// * Magnetized walls (snap to wall) with anchor lines
import { useDebugContext } from 'Web/Context/Debug/DebugContext';
import { AnchorLine, useGestureContext } from '../Context/GestureContext';
import { useMapContext } from '../Context/MapContext';
import { IRoom, RoomType } from '../Models/IRoom';
import { qSVG } from './qSVG';
import { CoordPoint, Equation, Wall } from './Types';
import { IRoomItem, RoomItemType } from '../Models/IRoomItem';
import { MapHelper } from '../Helpers/MapHelper';
import { PointUtils } from './PointUtils';
import { RoomItemUtils } from './RoomItem/RoomItemUtils';
import { Editor } from './Editor';

const MAGNETIZE_SENSIBILITY = 1;
const MAGNETIZE_SENSIBILITY_AXES = 1;
const MAGNETIZE_SENSIBILITY_RIGHT_ANGLE = 7;

function toAnchorLine(wall: Wall, id: string): AnchorLine {
    const eq: Equation = wall.equations.base;
    const start = qSVG.nearPointOnEquation(eq, { x: 0, y: 0 });
    const end = qSVG.nearPointOnEquation(eq, { x: 15000, y: 15000 });
    const anchorLine: AnchorLine = {
        id,
        start: start,
        end: end,
    };
    return anchorLine;
}

export const useMagnetize = () => {
    const gesture = useGestureContext();
    const map = useMapContext();
    const debugEnabled = useDebugContext().state.floorPlanEnabled;

    const magnetizedWalls = (
        snap: CoordPoint,
        bindedWall: Wall | { start: CoordPoint; end: CoordPoint; angleRad: number; roomId: string },
        walls: Array<Wall>,
        orthoSnapWallToMagnetize: Wall | undefined = undefined,
        withAnchorLines: boolean = true
    ): CoordPoint | undefined => {
        let computedAnchorLine: AnchorLine | undefined;
        let magnetizedPoint: CoordPoint | undefined;

        // Filter walls based on room type and exclude same room if applicable
        const roomId = 'roomId' in bindedWall ? bindedWall.roomId : undefined;
        // const rooms = map.getState().rooms;
        // const bindedRoomType = roomId ? rooms?.find((x) => x.roomId === roomId)?.type : undefined;
        const wallsToCheck = walls.filter((wall) => wall.roomId !== roomId);

        if (orthoSnapWallToMagnetize) {
            const pointOnWall = qSVG.nearPointOnEquation(orthoSnapWallToMagnetize.equations.base, snap);

            if (pointOnWall.distance < MAGNETIZE_SENSIBILITY) {
                magnetizedPoint = pointOnWall;

                if (withAnchorLines) {
                    const anchorLineId = `anchor¤${orthoSnapWallToMagnetize.roomId}¤${orthoSnapWallToMagnetize.sideName}`;
                    computedAnchorLine = toAnchorLine(orthoSnapWallToMagnetize, anchorLineId);
                }
            }
        }

        if (!magnetizedPoint) {
            // Check for bindedWall start/end point magnetization to other walls
            for (const wall of wallsToCheck) {
                // Check point to point magnetization
                const startStartMeasure = qSVG.measure(wall.start, bindedWall.start);
                if (startStartMeasure < MAGNETIZE_SENSIBILITY) {
                    const offset = qSVG.vectorXY(bindedWall.start, wall.start);
                    magnetizedPoint = PointUtils.translation(snap, offset);
                    break;
                }
                const endStartMeasure = qSVG.measure(wall.start, bindedWall.end);
                if (endStartMeasure < MAGNETIZE_SENSIBILITY) {
                    const offset = qSVG.vectorXY(bindedWall.end, wall.start);
                    magnetizedPoint = PointUtils.translation(snap, offset);
                    break;
                }
                const startEndMeasure = qSVG.measure(wall.end, bindedWall.start);
                if (startEndMeasure < MAGNETIZE_SENSIBILITY) {
                    const offset = qSVG.vectorXY(bindedWall.start, wall.end);
                    magnetizedPoint = PointUtils.translation(snap, offset);
                    break;
                }
                const endEndMeasure = qSVG.measure(wall.end, bindedWall.end);
                if (endEndMeasure < MAGNETIZE_SENSIBILITY) {
                    const offset = qSVG.vectorXY(bindedWall.end, wall.end);
                    magnetizedPoint = PointUtils.translation(snap, offset);
                    break;
                }

                // Check bindedWall points against entire wall
                const startPointOnWall = qSVG.nearPointOnEquation(wall.equations.base, bindedWall.start);
                if (startPointOnWall.distance < MAGNETIZE_SENSIBILITY) {
                    const offset = qSVG.vectorXY(bindedWall.start, startPointOnWall);
                    magnetizedPoint = PointUtils.translation(snap, offset);
                    if (withAnchorLines) {
                        const anchorLineId = `anchor¤${wall.roomId}¤${wall.sideName}`;
                        computedAnchorLine = toAnchorLine(wall, anchorLineId);
                    }
                    break;
                }

                const endPointOnWall = qSVG.nearPointOnEquation(wall.equations.base, bindedWall.end);
                if (endPointOnWall.distance < MAGNETIZE_SENSIBILITY) {
                    const offset = qSVG.vectorXY(bindedWall.end, endPointOnWall);
                    magnetizedPoint = PointUtils.translation(snap, offset);
                    if (withAnchorLines) {
                        const anchorLineId = `anchor¤${wall.roomId}¤${wall.sideName}`;
                        computedAnchorLine = toAnchorLine(wall, anchorLineId);
                    }
                    break;
                }
            }
        }

        // Update anchor lines if debug is enabled and they changed
        if (
            withAnchorLines &&
            (gesture.state.verticalAnchorLine?.id !== computedAnchorLine?.id ||
                gesture.state.horizontalAnchorLine?.id !== computedAnchorLine?.id)
        ) {
            gesture.update({
                verticalAnchorLine: computedAnchorLine, //todo handle magnetized from 2 different angles
            });
        }

        return magnetizedPoint;
    };

    const magnetizedWallPoint = (
        snap: CoordPoint,
        roomId: string,
        walls: Array<Wall>,
        parentWall: Wall,
        childWall: Wall
    ): CoordPoint | undefined => {
        const rooms = map.getState().rooms;
        const type = rooms?.find((x) => x.roomId === roomId)?.type;
        const roomsToUse = roomId ? rooms?.filter((x) => x.roomId !== roomId && x.type === type) : rooms;
        let magnetVertical: number | undefined;
        if (Math.abs(snap.x - parentWall.start.x) < MAGNETIZE_SENSIBILITY_AXES) {
            magnetVertical = parentWall.start.x;
        }
        if (Math.abs(snap.x - childWall.end.x) < MAGNETIZE_SENSIBILITY_AXES) {
            magnetVertical = childWall.end.x;
        }
        let magnetHorizontal: number | undefined;
        if (Math.abs(snap.y - parentWall.start.y) < MAGNETIZE_SENSIBILITY_AXES) {
            magnetHorizontal = parentWall.start.y;
        }
        if (Math.abs(snap.y - childWall.end.y) < MAGNETIZE_SENSIBILITY_AXES) {
            magnetHorizontal = childWall.end.y;
        }
        for (const wall of walls.filter((x) => roomsToUse?.some((r) => r.roomId === x.roomId))) {
            //* diagonal angles are not supported
            const pointOnWall = qSVG.nearPointOnEquation(wall.equations.base, snap);
            if (qSVG.measure(wall.start, snap) < MAGNETIZE_SENSIBILITY) {
                return {
                    x: magnetVertical ? magnetVertical : wall.start.x,
                    y: magnetHorizontal ? magnetHorizontal : wall.start.y,
                };
            }
            if (qSVG.measure(wall.end, snap) < MAGNETIZE_SENSIBILITY) {
                return {
                    x: magnetVertical ? magnetVertical : wall.end.x,
                    y: magnetHorizontal ? magnetHorizontal : wall.end.y,
                };
            }
            if (pointOnWall.distance < MAGNETIZE_SENSIBILITY) {
                return {
                    x: magnetVertical ? magnetVertical : pointOnWall.x,
                    y: magnetHorizontal ? magnetHorizontal : pointOnWall.y,
                };
            }
        }
        // Magnetize to enforce 90° angle between parentWall and childWall.
        // By Thales' theorem, the locus of all points P where the angle ∠(parentWall.start, P, childWall.end) = 90°
        // is the circle whose diameter is [parentWall.start, childWall.end].
        // The nearest point on that circle to snap is the correct 90° snap point.
        {
            // Log current angle at snap
            const circleCenter: CoordPoint = {
                x: (parentWall.start.x + childWall.end.x) / 2,
                y: (parentWall.start.y + childWall.end.y) / 2,
            };
            const circleRadius = qSVG.measure(parentWall.start, childWall.end) / 2;
            const toSnap = { x: snap.x - circleCenter.x, y: snap.y - circleCenter.y };
            const toSnapDist = Math.sqrt(toSnap.x * toSnap.x + toSnap.y * toSnap.y);
            if (toSnapDist > 0 && Math.abs(toSnapDist - circleRadius) < MAGNETIZE_SENSIBILITY_RIGHT_ANGLE) {
                const orthoSnapPoint: CoordPoint = {
                    x: circleCenter.x + circleRadius * (toSnap.x / toSnapDist),
                    y: circleCenter.y + circleRadius * (toSnap.y / toSnapDist),
                };
                return {
                    x: magnetVertical ? magnetVertical : orthoSnapPoint.x,
                    y: magnetHorizontal ? magnetHorizontal : orthoSnapPoint.y,
                };
            }
        }

        if (magnetVertical || magnetHorizontal) {
            return {
                x: magnetVertical ? magnetVertical : snap.x,
                y: magnetHorizontal ? magnetHorizontal : snap.y,
            };
        }
        return undefined;
    };

    // * Magnetized rooms (snap to wall) with anchor lines

    // * Description :
    // * Cette méthode renvoie un nouveau snap, qui doit être utiliser pour overrider le snap courant.
    // * Si la pièce n'est pas déplacée dans l'interval de sensibilité (MAGNETIZE_SENSIBILITY) d'un mur d'une autre pièce, les propriétés (x ou y) de forcedSnap sont undefined.

    // * Réalise un tri des murs de la pièce séléctionnée : murs verticaux et murs horizontaux (bindedRoomHorizontalWalls, bindedRoomHorizontalWalls)
    // * On regarde les murs des autres pièces,
    // * on prend le mur qui est le plus proche verticalement ou horizontalement dans les liste bindedRoomVerticalWalls ou bindedRoomHorizontalWalls
    // * on regarde si le mur qu'on a pris précédement rentre dans l'interval de sensibilité (MAGNETIZE_SENSIBILITY)
    // * si c'est le cas on sauvegarde sa coordonnée verticale ou horizontale et on calcul un nouveau snap : forceSnap

    const magnetizedRooms = (
        snap: CoordPoint,
        bindedRoom: {
            walls: Array<{ start: CoordPoint; end: CoordPoint; angleRad: number }>;
            roomId?: string;
            type?: RoomType | RoomItemType;
        },
        translation: CoordPoint,
        initialSnapPosition: CoordPoint,
        rooms: IRoom[],
        withAnchorLines: boolean = true
    ) => {
        const bindedRoomWalls = bindedRoom.walls || [];
        let magnetizedPoint: CoordPoint | undefined;
        const roomsFiltred =
            bindedRoom.roomId && bindedRoom.type
                ? rooms.filter((r) => r.type === bindedRoom.type && r.roomId !== bindedRoom.roomId)
                : rooms;

        const translationVector = qSVG.vectorXY(initialSnapPosition, snap);
        for (const room of roomsFiltred) {
            const roomWalls = room.walls || [];
            for (const wall of roomWalls) {
                for (const bindedRoomWall of bindedRoomWalls) {
                    const startStartMeasure = qSVG.measure(wall.start, bindedRoomWall.start);
                    if (startStartMeasure < MAGNETIZE_SENSIBILITY) {
                        const offset = qSVG.vectorXY(bindedRoomWall.start, wall.start);
                        magnetizedPoint = PointUtils.translation(snap, offset);
                        return magnetizedPoint;
                    }

                    const endStartMeasure = qSVG.measure(wall.start, bindedRoomWall.end);
                    if (endStartMeasure < MAGNETIZE_SENSIBILITY) {
                        const offset = qSVG.vectorXY(bindedRoomWall.end, wall.start);
                        magnetizedPoint = PointUtils.translation(snap, offset);
                        return magnetizedPoint;
                    }
                    const startEndMeasure = qSVG.measure(wall.end, bindedRoomWall.start);
                    if (startEndMeasure < MAGNETIZE_SENSIBILITY) {
                        const offset = qSVG.vectorXY(bindedRoomWall.start, wall.end);
                        magnetizedPoint = PointUtils.translation(snap, offset);
                        return magnetizedPoint;
                    }
                    const endEndMeasure = qSVG.measure(wall.end, bindedRoomWall.end);
                    if (endEndMeasure < MAGNETIZE_SENSIBILITY) {
                        const offset = qSVG.vectorXY(bindedRoomWall.end, wall.end);
                        magnetizedPoint = PointUtils.translation(snap, offset);
                        return magnetizedPoint;
                    }

                    const snappedBindedRoomWalStart = PointUtils.translation(bindedRoomWall.start, translationVector);
                    const startPointOnWall = qSVG.nearPointOnEquation(wall.equations.base, snappedBindedRoomWalStart);
                    if (
                        startPointOnWall.distance < MAGNETIZE_SENSIBILITY &&
                        Editor.testPointBelongToWall(wall, startPointOnWall)
                    ) {
                        const offset = qSVG.vectorXY(snappedBindedRoomWalStart, startPointOnWall);
                        magnetizedPoint = PointUtils.translation(snap, offset);
                        return magnetizedPoint;
                    }
                }
            }
        }
        return magnetizedPoint;
    };

    const magnetizedRoomItem = (
        snap: CoordPoint,
        bindedRoomItem: IRoomItem,
        initialRoomItemCoords: Array<CoordPoint>,
        initialSnap: CoordPoint,
        rooms: IRoom[]
    ) => {
        const walls = MapHelper.getWalls(rooms);
        const roomItems = MapHelper.getRoomItems(rooms).filter((x) => x.roomItemId !== bindedRoomItem.roomItemId);
        const roomItemsWalls = roomItems.flatMap((ri) => RoomItemUtils.getRoomItemPartialWalls(ri));

        const vectorSnap = qSVG.vectorXY(initialSnap, snap);

        const bindedRoomItemProjectedCoords = initialRoomItemCoords.map((x) =>
            PointUtils.translation(x, vectorSnap as CoordPoint)
        );

        // Try to magnetize to walls first
        let snapToWall;
        for (const wall of walls) {
            for (const coord of bindedRoomItemProjectedCoords) {
                if (qSVG.measure(wall.start, coord) < MAGNETIZE_SENSIBILITY) {
                    const offsetSnap = qSVG.vectorXY(coord, wall.start);
                    console.log('magnetizedRoomItem', coord, wall.start, offsetSnap);
                    return PointUtils.translation(snap, offsetSnap);
                }

                const pointOnWall = qSVG.nearPointOnEquation(wall.equations.base, coord);
                if (pointOnWall.distance < MAGNETIZE_SENSIBILITY && Editor.testPointBelongToWall(wall, pointOnWall)) {
                    const offsetSnap = qSVG.vectorXY(coord, pointOnWall);
                    snapToWall = PointUtils.translation(snap, offsetSnap);
                }
            }
        }
        if (snapToWall) {
            return snapToWall;
        }

        // If no wall magnetization, try to magnetize to other room items
        for (const wall of roomItemsWalls) {
            for (const coord of bindedRoomItemProjectedCoords) {
                if (qSVG.measure(wall.start, coord) < MAGNETIZE_SENSIBILITY) {
                    const offsetSnap = qSVG.vectorXY(coord, wall.start);
                    return PointUtils.translation(snap, offsetSnap);
                }

                const pointOnWall = qSVG.nearPointOnEquation(wall.equations.base, coord);
                if (pointOnWall.distance < MAGNETIZE_SENSIBILITY && Editor.testPointBelongToWall(wall, pointOnWall)) {
                    const offsetSnap = qSVG.vectorXY(coord, pointOnWall);
                    snapToWall = PointUtils.translation(snap, offsetSnap);
                }
            }
        }
        if (snapToWall) {
            return snapToWall;
        }

        return undefined;
    };

    const magnetizedRoomItemPoint = (
        snap: CoordPoint,
        bindedRoomItem: IRoomItem,
        rooms: IRoom[]
    ): CoordPoint | undefined => {
        const walls = MapHelper.getWalls(rooms);
        const roomItems = MapHelper.getRoomItems(rooms).filter((x) => x.roomItemId !== bindedRoomItem.roomItemId);
        const roomItemsWalls = roomItems.flatMap((ri) => RoomItemUtils.getRoomItemPartialWalls(ri));

        // Try to magnetize to walls first
        for (const wall of walls) {
            const pointOnWall = qSVG.nearPointOnEquation(wall.equations.base, snap);
            if (pointOnWall.distance < MAGNETIZE_SENSIBILITY && Editor.testPointBelongToWall(wall, pointOnWall)) {
                return pointOnWall;
            }
        }

        // If no wall magnetization, try to magnetize to other room items
        for (const wall of roomItemsWalls) {
            const pointOnWall = qSVG.nearPointOnEquation(wall.equations.base, snap);
            if (pointOnWall.distance < MAGNETIZE_SENSIBILITY && Editor.testPointBelongToWall(wall, pointOnWall)) {
                return pointOnWall;
            }
        }
        return undefined;
    };

    return { magnetizedWalls, magnetizedWallPoint, magnetizedRooms, magnetizedRoomItem, magnetizedRoomItemPoint };
};
